import type { AnalysisResult, ScoreBar, ScoreDoc, ScoreObject, ScorePart } from '../types';

export const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const FLAT_TO_SHARP: Record<string, string> = { Db: 'C#', Eb: 'D#', Gb: 'F#', Ab: 'G#', Bb: 'A#' };
const KEY_INDEX: Record<string, number> = Object.fromEntries(NOTES.map((note, index) => [note, index]));

export function uid(prefix = 'item') {
  return `${prefix}-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`}`;
}

export function createEmptyScore(title = '未命名樂譜'): ScoreDoc {
  const bars: ScoreBar[] = Array.from({ length: 80 }, (_, index) => ({
    id: uid('bar'), chord: '', degree: '', lyric: '', section: index === 0 ? 'Intro' : '', notes: '', drum: '',
  }));
  const parts: ScorePart[] = [
    { id: uid('part'), name: '主旋律', type: 'melody', visible: true, bars: bars.map(() => '') },
    { id: uid('part'), name: '和聲', type: 'harmony', visible: true, bars: bars.map(() => '') },
    { id: uid('part'), name: '節奏', type: 'rhythm', visible: true, bars: bars.map(() => '') },
    { id: uid('part'), name: '鼓組', type: 'drum', visible: false, bars: bars.map(() => '') },
  ];
  return {
    id: uid('score'), title, key: 'D', mode: 'major', bpm: 120, timeSignature: '4/4',
    barsPerRow: 8, rowsPerPage: 10, density: 'standard', viewMode: 'degree', pageMode: 'single', bars, parts,
    objects: [],
    layers: [
      { id: 'layer-pencil', name: '手寫標註', visible: true, color: '#e66b4e' },
      { id: 'layer-rehearsal', name: '排練重點', visible: true, color: '#5e8f78' },
    ],
    sections: ['Intro', 'A', 'B', 'C', 'Bridge', 'Chorus'], updatedAt: Date.now(),
  };
}

/** Backfill fields omitted by older, imported, or partially synced score documents. */
export function normalizeScore(value: unknown): ScoreDoc {
  const raw = value && typeof value === 'object' ? value as Partial<ScoreDoc> : {};
  const fallback = createEmptyScore(typeof raw.title === 'string' ? raw.title : '未命名樂譜');
  const bars = Array.isArray(raw.bars) ? raw.bars.filter((bar): bar is ScoreBar => !!bar && typeof bar === 'object').map((bar, index) => ({
    ...bar,
    id: typeof bar.id === 'string' ? bar.id : uid('bar'),
    chord: typeof bar.chord === 'string' ? bar.chord : '',
    degree: typeof bar.degree === 'string' ? bar.degree : '',
    lyric: typeof bar.lyric === 'string' ? bar.lyric : '',
    section: typeof bar.section === 'string' ? bar.section : (index === 0 ? 'Intro' : ''),
    notes: typeof bar.notes === 'string' ? bar.notes : '',
    drum: typeof bar.drum === 'string' ? bar.drum : '',
  })) : fallback.bars;
  const layers = Array.isArray(raw.layers) ? raw.layers.filter((layer) => !!layer && typeof layer === 'object').map((layer) => ({
    id: typeof layer.id === 'string' ? layer.id : uid('layer'),
    name: typeof layer.name === 'string' ? layer.name : '標註圖層',
    visible: typeof layer.visible === 'boolean' ? layer.visible : true,
    color: typeof layer.color === 'string' ? layer.color : '#5e8f78',
  })) : fallback.layers;
  const parts = Array.isArray(raw.parts) ? raw.parts.filter((part) => !!part && typeof part === 'object').map((part) => ({
    id: typeof part.id === 'string' ? part.id : uid('part'),
    name: typeof part.name === 'string' ? part.name : '聲部',
    type: part.type === 'harmony' || part.type === 'rhythm' || part.type === 'drum' ? part.type : 'melody' as const,
    visible: typeof part.visible === 'boolean' ? part.visible : true,
    bars: Array.isArray(part.bars) ? part.bars.map((item) => typeof item === 'string' ? item : '') : bars.map(() => ''),
  })) : fallback.parts.map((part) => ({ ...part, bars: bars.map(() => '') }));
  const objects = Array.isArray(raw.objects) ? raw.objects.filter((object) => !!object && typeof object === 'object').map((object) => ({ ...object })) as ScoreObject[] : [];
  return {
    ...fallback,
    ...raw,
    id: typeof raw.id === 'string' && raw.id ? raw.id : fallback.id,
    title: typeof raw.title === 'string' ? raw.title : fallback.title,
    key: typeof raw.key === 'string' ? raw.key : fallback.key,
    mode: raw.mode === 'minor' ? 'minor' : 'major',
    bpm: typeof raw.bpm === 'number' && Number.isFinite(raw.bpm) ? raw.bpm : fallback.bpm,
    timeSignature: typeof raw.timeSignature === 'string' ? raw.timeSignature : fallback.timeSignature,
    barsPerRow: typeof raw.barsPerRow === 'number' && raw.barsPerRow > 0 ? raw.barsPerRow : fallback.barsPerRow,
    rowsPerPage: typeof raw.rowsPerPage === 'number' && raw.rowsPerPage > 0 ? raw.rowsPerPage : fallback.rowsPerPage,
    density: raw.density === 'large' || raw.density === 'compact' || raw.density === 'dense' ? raw.density : 'standard',
    viewMode: raw.viewMode === 'chord' ? 'chord' : 'degree',
    pageMode: raw.pageMode === 'half-preview' ? 'half-preview' : 'single',
    bars,
    parts,
    objects,
    layers,
    sections: Array.isArray(raw.sections) ? raw.sections.filter((section): section is string => typeof section === 'string') : fallback.sections,
    updatedAt: typeof raw.updatedAt === 'number' && Number.isFinite(raw.updatedAt) ? raw.updatedAt : Date.now(),
    sharedWith: Array.isArray(raw.sharedWith) ? raw.sharedWith : [],
  };
}

function rootOfChord(chord: string): { note: string; suffix: string } | undefined {
  const match = chord.trim().match(/^([A-Ga-g])([#b]?)(.*)$/);
  if (!match) return undefined;
  const raw = `${match[1].toUpperCase()}${match[2]}`;
  return { note: FLAT_TO_SHARP[raw] ?? raw, suffix: match[3].split('/')[0].trim() };
}

function chordQuality(suffix: string, defaultQuality: string) {
  const normalized = suffix.replace(/[()]/g, '').toLowerCase();
  if (/dim|°|o/.test(normalized)) return 'dim';
  if (/aug|\+/.test(normalized)) return 'aug';
  if (/maj/.test(normalized)) return 'maj';
  if (/m(?!aj)/.test(normalized)) return 'm';
  if (/sus/.test(normalized)) return 'sus';
  if (/7|9|11|13/.test(normalized)) return suffix;
  return defaultQuality;
}

export function chordToDegree(chord: string, key: string, mode: 'major' | 'minor' = 'major') {
  const parsed = rootOfChord(chord);
  const tonic = KEY_INDEX[FLAT_TO_SHARP[key] ?? key] ?? 0;
  if (!parsed) return chord;
  const noteIndex = KEY_INDEX[parsed.note];
  if (noteIndex === undefined) return chord;
  const semitone = (noteIndex - tonic + 12) % 12;
  const major = [
    { semis: 0, label: '1', quality: 'maj' }, { semis: 2, label: '2', quality: 'm' },
    { semis: 4, label: '3', quality: 'm' }, { semis: 5, label: '4', quality: 'maj' },
    { semis: 7, label: '5', quality: 'maj' }, { semis: 9, label: '6', quality: 'm' },
    { semis: 11, label: '7', quality: 'dim' },
  ];
  const minor = [
    { semis: 0, label: '1', quality: 'm' }, { semis: 2, label: '2', quality: 'dim' },
    { semis: 3, label: '3', quality: 'maj' }, { semis: 5, label: '4', quality: 'm' },
    { semis: 7, label: '5', quality: 'm' }, { semis: 8, label: '6', quality: 'maj' },
    { semis: 10, label: '7', quality: 'maj' },
  ];
  const scale = mode === 'minor' ? minor : major;
  let match = scale.find((item) => item.semis === semitone);
  let alteration = '';
  if (!match) {
    const lower = scale.find((item) => (item.semis + 1) % 12 === semitone);
    const upper = scale.find((item) => (item.semis + 11) % 12 === semitone);
    if (lower) { match = lower; alteration = 'b'; }
    else if (upper) { match = upper; alteration = '#'; }
  }
  if (!match) return chord;
  const quality = chordQuality(parsed.suffix, 'maj');
  const qualitySuffix = quality === 'maj' ? '' : quality === 'm' ? 'm' : quality === 'dim' ? 'dim' : quality === 'aug' ? 'aug' : quality === 'sus' ? 'sus' : quality;
  return `${alteration}${match.label}${qualitySuffix}`;
}

export function inferKeyFromChords(
  chords: Array<{ text: string }>,
  preferredKey?: string,
  preferredMode?: 'major' | 'minor',
  evidence?: Pick<AnalysisResult, 'keySignature' | 'tonicMarker' | 'keyConfidence'>,
) {
  const usable = chords.map((chord) => rootOfChord(chord.text)).filter((chord): chord is NonNullable<typeof chord> => !!chord);
  const tonicMarker = rootOfChord((evidence?.tonicMarker ?? '').replace(/^\s*1\s*=\s*/, ''))?.note;
  const signature = evidence?.keySignature;
  const hasSharpCount = Number.isInteger(signature?.sharps) && Number(signature?.sharps) >= 0 && Number(signature?.sharps) <= 7;
  const hasFlatCount = Number.isInteger(signature?.flats) && Number(signature?.flats) >= 0 && Number(signature?.flats) <= 7;
  const hasKeySignature = !!signature && (hasSharpCount || hasFlatCount) && !(hasSharpCount && hasFlatCount && Number(signature.sharps) > 0 && Number(signature.flats) > 0);
  if (usable.length < 3 && !tonicMarker && !hasKeySignature) return undefined;
  const sharpMajorKeys = ['C', 'G', 'D', 'A', 'E', 'B', 'F#', 'C#'];
  const flatMajorKeys = ['C', 'F', 'A#', 'D#', 'G#', 'C#', 'F#', 'B'];
  const signatureMajor = hasKeySignature
    ? (Number(signature?.sharps) > 0 ? sharpMajorKeys[Number(signature?.sharps)] : flatMajorKeys[Number(signature?.flats)])
    : undefined;
  const signatureMajorIndex = signatureMajor ? KEY_INDEX[signatureMajor] : undefined;
  const signatureMinorIndex = signatureMajorIndex === undefined ? undefined : (signatureMajorIndex + 9) % 12;
  const scales = {
    major: { offsets: [0, 2, 4, 5, 7, 9, 11], qualities: ['maj', 'm', 'm', 'maj', 'maj', 'm', 'dim'] },
    minor: { offsets: [0, 2, 3, 5, 7, 8, 10], qualities: ['m', 'dim', 'maj', 'm', 'm', 'maj', 'maj'] },
  } as const;
  let best: { key: string; mode: 'major' | 'minor'; score: number } | undefined;
  for (const key of NOTES) for (const mode of ['major', 'minor'] as const) {
    const scale = scales[mode];
    const offsets: readonly number[] = scale.offsets;
    const qualities: readonly string[] = scale.qualities;
    let score = 0;
    const tonic = KEY_INDEX[key];
    if (tonicMarker) score += tonic === KEY_INDEX[tonicMarker] ? 7 : 0;
    if (signatureMajorIndex !== undefined && (tonic === signatureMajorIndex || tonic === signatureMinorIndex)) {
      score += 4 * Math.max(0.4, Math.min(1, Number(signature?.confidence ?? 0.75)));
    }
    usable.forEach((chord, index) => {
      const root = KEY_INDEX[chord.note];
      const interval = (root - tonic + 12) % 12;
      const degree = offsets.indexOf(interval);
      const suffix = chord.suffix;
      const actualQuality = /dim|°|o/i.test(suffix) ? 'dim'
        : /aug|\+/i.test(suffix) ? 'aug'
          : /sus/i.test(suffix) ? 'sus'
            : /m(?!aj)/i.test(suffix) ? 'm' : 'maj';
      if (degree < 0) { score -= 2.4; return; }
      score += 1.2;
      const expectedQuality = qualities[degree];
      score += actualQuality === expectedQuality || actualQuality === 'sus' ? 2.1 : -1.1;
      if (interval === 0) score += mode === 'major' ? 1.1 : 0.9;
      if (index === 0 && interval === 0) score += 0.8;
      if (index === usable.length - 1 && interval === 0) score += 3.2;
    });
    const dominantRoot = (KEY_INDEX[key] + 7) % 12;
    const hasMajorDominant = usable.some((chord) => KEY_INDEX[chord.note] === dominantRoot && !/m(?!aj)|dim|°|o|aug|\+|sus/i.test(chord.suffix));
    if (mode === 'minor') score += hasMajorDominant ? 2.2 : -4.2;
    else if (hasMajorDominant) score += 1.1;
    const finalChord = usable.at(-1);
    if (finalChord && KEY_INDEX[finalChord.note] === KEY_INDEX[key]) {
      const finalIsMinor = /m(?!aj)/i.test(finalChord.suffix);
      score += mode === 'minor' && finalIsMinor ? 3.6 : mode === 'major' && !finalIsMinor ? 4.6 : -1;
    }
    if ((FLAT_TO_SHARP[preferredKey ?? ''] ?? preferredKey) === key && preferredMode === mode) {
      score += 1.5 * Math.max(0.15, Math.min(1, Number(evidence?.keyConfidence ?? 0.35)));
    }
    if (!best || score > best.score) best = { key, mode, score };
  }
  return best;
}

function nearestChordBarIndex(x: number, y: number, chords: ScoreObject[]) {
  const located = chords.filter((object) => object.barIndex !== undefined && Number.isFinite(object.x) && Number.isFinite(object.y));
  if (!located.length) return undefined;
  const nearestY = Math.min(...located.map((object) => Math.abs(object.y - y)));
  // Lyrics and chord labels live on different baselines, but should still be
  // close vertically on the same printed staff system.
  if (nearestY > 0.2) return undefined;
  const sameRow = located.filter((object) => Math.abs(object.y - y) <= nearestY + 0.035);
  // Each lyric OCR item is one character. Assign it to the latest chord
  // anchor that begins at or before that character on the same staff row.
  // Nearest-neighbor assignment can pull the last character of a measure
  // into the next chord simply because its label is visually closer.
  const ordered = [...sameRow].sort((a, b) => a.x - b.x);
  // Chord symbols are centered/offset above the staff while lyrics are
  // centered under noteheads. Start the new chord's lyric range about one
  // printed character before the label x so the first syllable is not left
  // behind in the previous measure.
  const preceding = ordered.filter((object) => object.x <= x + 0.035).at(-1);
  const anchor = preceding ?? ordered[0];
  if (!anchor || Math.abs(anchor.x - x) > 0.22) return undefined;
  return anchor.barIndex;
}

export function applyAnalysis(score: ScoreDoc, result: AnalysisResult, includeLyrics = true, includeChords = true): ScoreDoc {
  const next = structuredClone(score);
  if (includeChords && result.chords?.length) {
    const chordLayer = next.layers.find((layer) => layer.id === 'detected-chords');
    if (chordLayer) chordLayer.visible = true;
    else next.layers.push({ id: 'detected-chords', name: 'AI 和弦標註', visible: true, color: '#ffe75e' });
  }
  if (includeLyrics && result.lyrics?.length && !next.layers.some((layer) => layer.id === 'detected-lyrics')) {
    next.layers.push({ id: 'detected-lyrics', name: 'AI 歌詞標註', visible: true, color: '#d9e8d9' });
  }
  if (includeChords) {
    for (const old of next.objects.filter((object) => object.kind === 'chord' && (object.source === 'detected' || object.source === 'converted'))) {
      if (old.barIndex !== undefined && next.bars[old.barIndex]?.chord === old.text) next.bars[old.barIndex] = { ...next.bars[old.barIndex], chord: '', degree: '' };
    }
    next.objects = next.objects.filter((object) => object.kind !== 'chord' || (object.source !== 'detected' && object.source !== 'converted'));
  }
  if (includeLyrics) clearDetectedLyricsInPlace(next);
  if (result.title) next.title = result.title;
  if (includeChords && result.chords?.length) {
    const keyConfidenceIsUsable = typeof result.keyConfidence !== 'number' || result.keyConfidence >= 0.55;
    if (keyConfidenceIsUsable) {
      const inferredKey = inferKeyFromChords(result.chords, result.key, result.mode, result);
      if (inferredKey) { next.key = inferredKey.key; next.mode = inferredKey.mode; }
      else if (result.key) next.key = result.key;
    }
  }
  if (result.bpm && result.bpm > 0) next.bpm = result.bpm;
  if (result.timeSignature) next.timeSignature = result.timeSignature;
  const detectedChords = includeChords ? result.chords ?? [] : [];
  const skippedSourceBars: Array<{ after: number; count: number }> = [];
  let previousSourceBar = -1;
  let skippedBars = 0;
  const sourceBarToChartBar = (sourceBar: number) => sourceBar - skippedSourceBars
    .filter((gap) => sourceBar > gap.after)
    .reduce((total, gap) => total + gap.count, 0);
  for (const detected of detectedChords) {
    const preferredBar = typeof detected.barIndex === 'number' && detected.barIndex >= 0 ? detected.barIndex : barIndexFromPosition(detected.x, detected.y, score);
    if (previousSourceBar >= 0 && preferredBar > previousSourceBar + 1) {
      const missing = Math.floor(preferredBar - previousSourceBar - 1);
      skippedSourceBars.push({ after: previousSourceBar, count: missing });
      skippedBars += missing;
    }
    previousSourceBar = preferredBar;
    // Keep the model's measure assignment. Pushing a collision into the next
    // free bar shifts every later chord and creates misleading empty measures.
    const barIndex = Math.max(0, Math.min(next.bars.length - 1, Math.floor(preferredBar - skippedBars)));
    const object: ScoreObject = {
      id: uid('chord'), kind: 'chord', x: normalizedCoordinate(detected.x), y: normalizedCoordinate(detected.y),
      w: clamp(normalizedCoordinate(detected.w ?? 0.065), 0.012, 0.2), h: clamp(normalizedCoordinate(detected.h ?? 0.026), 0.012, 0.08),
      text: detected.text, color: '#ffe75e', layerId: 'detected-chords', page: 0,
      barIndex, confidence: detected.confidence ?? 0.72,
      source: 'detected', confirmed: false, fontSize: 19,
    };
    next.objects.push(object);
    if (next.bars[barIndex]) {
      const bar = next.bars[barIndex];
      bar.chord = detected.text;
      bar.degree = chordToDegree(detected.text, next.key, next.mode);
    }
  }
  if (includeChords) {
    next.bars = next.bars.map((bar) => ({ ...bar, degree: bar.chord ? chordToDegree(bar.chord, next.key, next.mode) : bar.degree }));
  }
  const lyricsByBar = new Map<number, Array<{ text: string; x: number }>>();
  const sourceChordObjects = next.objects.filter((object) => object.kind === 'chord');
  const detectedLyrics = includeLyrics ? result.lyrics ?? [] : [];
  const positionedLyrics = detectedLyrics.flatMap((detected) => {
    const glyphs = Array.from(detected.text ?? '');
    if (glyphs.length <= 1) return [detected];
    // Gemini can occasionally ignore the one-character output instruction
    // and return a whole phrase. Treat x as the first glyph's center and
    // spread later glyphs forward before aligning each one to chord anchors.
    const width = detected.w !== undefined
      ? normalizedCoordinate(detected.w)
      : glyphs.reduce((total, glyph) => total + (/^[\u0000-\u00ff]$/.test(glyph) ? 0.018 : 0.035), 0);
    const step = width / glyphs.length;
    return glyphs.map((text, index) => ({
      ...detected,
      text,
      x: normalizedCoordinate(detected.x) + step * index,
    }));
  });
  for (const detected of positionedLyrics) {
    const hasModelBarIndex = typeof detected.barIndex === 'number' && Number.isInteger(detected.barIndex) && detected.barIndex >= 0;
    const sourceBarIndex = hasModelBarIndex ? detected.barIndex! : barIndexFromPosition(detected.x, detected.y, score);
    const x = normalizedCoordinate(detected.x);
    const y = normalizedCoordinate(detected.y);
    const spatialBarIndex = nearestChordBarIndex(x, y, sourceChordObjects);
    const alignedBarIndex = spatialBarIndex ?? (hasModelBarIndex && result.lyricBarIndexSpace === 'chart'
      ? sourceBarIndex
      : sourceBarToChartBar(sourceBarIndex));
    const barIndex = Math.max(0, Math.min(next.bars.length - 1, alignedBarIndex));
    const object: ScoreObject = {
      id: uid('lyric'), kind: 'lyric', x, y,
      w: 0.11, h: 0.035, text: detected.text, color: '#2d302c',
      layerId: 'detected-lyrics', page: 0, barIndex,
      source: 'detected', confirmed: false, fontSize: 16,
    };
    next.objects.push(object);
    if (next.bars[barIndex]) {
      const fragments = lyricsByBar.get(barIndex) ?? [];
      fragments.push({ text: detected.text, x: detected.x });
      lyricsByBar.set(barIndex, fragments);
    }
  }
  for (const [barIndex, fragments] of lyricsByBar) {
    next.bars[barIndex].lyric = fragments.sort((left, right) => left.x - right.x).map((fragment) => fragment.text).join('');
  }
  for (const section of result.sections ?? []) {
    if (next.bars[section.barIndex]) next.bars[section.barIndex].section = section.name;
    if (!next.sections.includes(section.name)) next.sections.push(section.name);
  }
  if (result.beatTimes) next.beatTimes = result.beatTimes.filter((time) => Number.isFinite(time) && time >= 0).slice(0, 10000);
  if (result.audioDuration) next.audioDuration = result.audioDuration;
  next.updatedAt = Date.now();
  return next;
}

export function clearDetectedLyrics(score: ScoreDoc): ScoreDoc {
  const next = structuredClone(score);
  clearDetectedLyricsInPlace(next);
  next.updatedAt = Date.now();
  return next;
}

function clearDetectedLyricsInPlace(score: ScoreDoc) {
  const previousDetectedLyrics = new Map<number, ScoreObject[]>();
  for (const object of score.objects.filter((item) => item.kind === 'lyric' && item.source === 'detected' && item.barIndex !== undefined)) {
    const group = previousDetectedLyrics.get(object.barIndex!) ?? [];
    group.push(object);
    previousDetectedLyrics.set(object.barIndex!, group);
  }
  for (const [barIndex, objects] of previousDetectedLyrics) {
    const previousText = objects.sort((a, b) => a.x - b.x).map((object) => object.text ?? '').join('');
    // A bar lyric is the concatenation of every detected character in that
    // bar, not equal to any single character object. Clear stale AI text while
    // preserving a manual edit that no longer matches its generated objects.
    if (score.bars[barIndex]?.lyric === previousText) score.bars[barIndex] = { ...score.bars[barIndex], lyric: '' };
  }
  score.objects = score.objects.filter((object) => object.kind !== 'lyric' || object.source !== 'detected');
}

function barIndexFromPosition(x: number, y: number, score: ScoreDoc) {
  const row = Math.max(0, Math.min(score.rowsPerPage - 1, Math.floor((y - 0.13) / 0.077)));
  const column = Math.max(0, Math.min(score.barsPerRow - 1, Math.floor((x - 0.072) / (0.856 / score.barsPerRow))));
  return row * score.barsPerRow + column;
}

// Vision models may return normalized (0–1), percentage (0–100), or Gemini's
// common 0–1000 image coordinate values. Convert before clamping, otherwise
// every 0–1000 coordinate becomes 1 and the annotation is clipped at the edge.
function normalizedCoordinate(value: number) {
  const coordinate = Number.isFinite(value) ? value : 0;
  if (coordinate > 100 && coordinate <= 1000) return clamp(coordinate / 1000);
  if (coordinate > 1 && coordinate <= 100) return clamp(coordinate / 100);
  return clamp(coordinate);
}

export function plainTextToDegree(chord: string, score: ScoreDoc) {
  return chordToDegree(chord, score.key, score.mode);
}

export function clamp(value: number, min = 0, max = 1) {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : 0));
}
